/**
 * ตัวช่วยอ่าน/เขียน endpoint ใหม่ของรอบ backend D-179…D-184 (บัญชี, โอนย้ายภายใน, worklist, กล่องงาน,
 * Work/Cost Center, นำเข้าสินค้า) — ทุกตัวคืน `{ items: [...] }` จึงอ่านเป็นแถวดิบแล้วให้หน้าจอเลือกคอลัมน์เอง
 */

import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';
import { callJson, type StockFailure, type StockRequestOptions } from '../inventory/stock';

export type RecordRow = Readonly<Record<string, unknown>>;

export type RecordsResult =
  | { readonly status: 'ready'; readonly items: readonly RecordRow[]; readonly extra: RecordRow }
  | StockFailure;
export type PostResult = { readonly status: 'ready'; readonly body: RecordRow } | StockFailure;

function isRecord(value: unknown): value is RecordRow {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function queryString(params: Readonly<Record<string, string | undefined>>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value.trim() !== '') {
      search.append(key, value.trim());
    }
  }
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
}

export async function fetchRecords(
  path: string,
  params: Readonly<Record<string, string | undefined>> = {},
  options: StockRequestOptions = {},
): Promise<RecordsResult> {
  const result = await callJson('GET', `${path}${queryString(params)}`, undefined, {}, options);
  if (result.status !== 'ok') {
    return result;
  }
  if (!isRecord(result.payload) || !Array.isArray(result.payload.items) || !result.payload.items.every(isRecord)) {
    return { status: 'invalid-response' };
  }
  const { items, ...extra } = result.payload;
  return { status: 'ready', items: items as readonly RecordRow[], extra };
}

export async function postRecord(
  path: string,
  body: unknown,
  headers: Readonly<Record<string, string>> = {},
  options: StockRequestOptions = {},
): Promise<PostResult> {
  const result = await callJson('POST', path, body, headers, options);
  if (result.status !== 'ok') {
    return result;
  }
  return isRecord(result.payload) ? { status: 'ready', body: result.payload } : { status: 'invalid-response' };
}

export async function patchRecord(
  path: string,
  body: unknown,
  headers: Readonly<Record<string, string>> = {},
  options: StockRequestOptions = {},
): Promise<PostResult> {
  const result = await callJson('PATCH', path, body, headers, options);
  if (result.status !== 'ok') {
    return result;
  }
  return isRecord(result.payload) ? { status: 'ready', body: result.payload } : { status: 'invalid-response' };
}

export const XLSX_MEDIA_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export interface ImportRowError {
  readonly row: number;
  readonly column: string;
  readonly rule: string;
  readonly message: string;
}

export interface ProductImportOutcome {
  readonly created: boolean;
  readonly row_count: number;
  readonly errors: readonly ImportRowError[];
  readonly product_codes: readonly string[];
}

export type ProductImportResult =
  | { readonly status: 'ready'; readonly outcome: ProductImportOutcome }
  | { readonly status: 'refused'; readonly message: string; readonly errors: readonly ImportRowError[] }
  | StockFailure;

export const PRODUCT_IMPORT_PATH = '/v1/product-imports';

function parseErrors(raw: unknown): ImportRowError[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter(isRecord).map((e) => ({
    row: typeof e.row === 'number' ? e.row : 0,
    column: typeof e.column === 'string' ? e.column : '',
    rule: typeof e.rule === 'string' ? e.rule : '',
    message: typeof e.message === 'string' ? e.message : '',
  }));
}

/** อัปโหลด .xlsx เป็น body ดิบ — `commit=false` คือ preview (ไม่เขียนอะไร) */
export async function uploadProductWorkbook(
  file: Blob,
  commit: boolean,
  options: StockRequestOptions = {},
): Promise<ProductImportResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  const url = buildApiUrl(
    options.apiBaseUrl ?? getConfiguredApiBaseUrl(),
    commit ? PRODUCT_IMPORT_PATH : `${PRODUCT_IMPORT_PATH}/preview`,
  );
  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', 'Content-Type': XLSX_MEDIA_TYPE },
      body: file,
    });
  } catch (error) {
    return { status: 'network-error', detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ' };
  }
  if (response.status === 401) {
    return { status: 'unauthenticated' };
  }
  if (response.status === 403) {
    return { status: 'forbidden' };
  }
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    /* ไม่มี body ที่อ่านได้ */
  }
  const body = isRecord(payload) ? payload : {};
  if (response.status === 422) {
    const details = isRecord(body.details) ? body.details : {};
    return {
      status: 'refused',
      message: typeof body.message === 'string' ? body.message : 'ไฟล์ถูกปฏิเสธ',
      errors: parseErrors(details.errors),
    };
  }
  if (response.status >= 500 || !response.ok) {
    return response.status >= 500
      ? { status: 'server-error', httpStatus: response.status }
      : {
          status: 'rejected',
          httpStatus: response.status,
          rule: null,
          message: typeof body.message === 'string' ? body.message : null,
        };
  }
  if (typeof body.created !== 'boolean') {
    return { status: 'invalid-response' };
  }
  return {
    status: 'ready',
    outcome: {
      created: body.created,
      row_count: typeof body.row_count === 'number' ? body.row_count : 0,
      errors: parseErrors(body.errors),
      product_codes: Array.isArray(body.product_codes) ? body.product_codes.map(String) : [],
    },
  };
}
